// src/store/reducers/reducerHelpers/thunkDelete.ts
import { axiosClientNoCache, csrf } from '@lib/axiosClientCached.ts';
import { processStoreError } from '@lib/errorHandler'; // Import the error processor
import { createAsyncThunk } from '@reduxjs/toolkit';
import { selectCurrentCompanySlug } from '@store/reducers/meReducer.ts';
import { addErrorResponseNotification } from '@store/reducers/notificationReducer.ts';
import {
  type InterfaceFunctionReplace,
  type InterfaceVarReplace,
  replaceUrlState,
  replaceUrlVariable,
  safeDispatchActions,
} from '@store/reducers/reducerHelpers/thunkHelperShared.ts';

import type { DispatchActionType } from '@/types/DispatchActionType.ts';

export interface InterfaceThunkDeleteData<T = undefined, U = undefined | InterfaceVarReplace> {
  form?: T;
  urlVarsReplaceMap?: U;
}

const genericTypeThunkDeleteReplace = <T = undefined, U = undefined | InterfaceVarReplace>(
  type: string,
  url: string,
  replaceMap: InterfaceFunctionReplace = {},
  successDispatchArray: DispatchActionType[] = [],
  errorDispatchArray: DispatchActionType[] = [],
) =>
  createAsyncThunk(
    `${type}/delete/${url}`,
    async (data: InterfaceThunkDeleteData<T, U> | undefined, { rejectWithValue, dispatch, getState }) => {
      let fullUrl = replaceUrlState(url, replaceMap, getState);
      if (data?.urlVarsReplaceMap !== undefined) {
        fullUrl = replaceUrlVariable(fullUrl, data.urlVarsReplaceMap ?? {});
      }
      await csrf();
      let axiosDelCfg = undefined;
      if (data?.form !== undefined) {
        axiosDelCfg = {
          data: data.form,
        };
      }
      const res = await axiosClientNoCache.delete(fullUrl, axiosDelCfg).catch((error) => {
        dispatch(addErrorResponseNotification({ error, rejectWithValue: undefined })); // rejectWithValue not used by notification here
        safeDispatchActions(dispatch, errorDispatchArray);
        const processedError = processStoreError(error);
        throw rejectWithValue({
          error: {
            message: processedError.message,
            statusCode: processedError.statusCode,
            data: processedError.data,
          },
        });
      });
      safeDispatchActions(dispatch, successDispatchArray);
      return res.data.data;
    },
  );

const genericTypeThunkDelete = <T = undefined, U = undefined | InterfaceVarReplace>(
  type: string,
  url: string,
  successDispatchArray: DispatchActionType[] = [],
  errorDispatchArray: DispatchActionType[] = [],
) => genericTypeThunkDeleteReplace<T, U>(type, url, {}, successDispatchArray, errorDispatchArray);

const genericTypeThunkDeleteCompany = <T = undefined, U = undefined | InterfaceVarReplace>(
  type: string,
  url: string,
  successDispatchArray: DispatchActionType[] = [],
  errorDispatchArray: DispatchActionType[] = [],
) =>
  genericTypeThunkDeleteReplace<T, U>(
    type,
    url,
    {
      '{companySlug}': selectCurrentCompanySlug,
    },
    successDispatchArray,
    errorDispatchArray,
  );

export const thunkDelete = {
  replUrl: genericTypeThunkDeleteReplace,
  compUrl: genericTypeThunkDeleteCompany,
  del: genericTypeThunkDelete,
};

export default thunkDelete;
